import { defineComponent as _defineComponent } from 'vue'
import { inject, provide, reactive, toRefs } from "vue";
import { CommunityTalkUserViewModel } from "@/viewModels/communityTalk/communityTalkUserViewModel";
import ApiGetLoadWithAddMaster from "@/components/common/api/ApiGetLoadWithAddMaster.vue";
import TalkRoomContents from "@/components/communityTalk/talkRoom/TalkRoomContents.vue";
import TalkRoomDate from "@/components/communityTalk/talkRoom/TalkRoomDate.vue";
import ReplyMaster from "@/components/communityTalk/reply/ReplyMaster.vue";
import ReplyOpenDetailsButton from "@/components/communityTalk/reply/ReplyOpenDetailsButton.vue";
import ReplyList from "@/components/communityTalk/reply/ReplyList.vue";
import ReplyMoreButton from "@/components/communityTalk/reply/ReplyMoreButton.vue";
import NoReply from "@/components/communityTalk/reply/NoReply.vue";
import ReplyPost from "@/components/communityTalk/reply/ReplyPost.vue";
import { ApiGetLoadAddStatusType } from "@/modules/types/apiLoadStatusType";
import CommunityTalkUser from "@/components/communityTalk/CommunityTalkUser.vue";
import CommunityTalkMedia from "@/components/common/CommunityTalkMedia.vue";
import TalkRoomNice from "@/components/communityTalk/talkRoom/TalkRoomNice.vue";
import { LayoutTagGetResponse_WordSetting } from "@/apiRequests/models/communityTalk/layoutTagGetResponse";
import ReplyController from "@/controllers/communityTalk/replyController";
import { ApiGetLoadAddStatus } from "@/modules/types/apiLoadStatusType";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import { ReplyDto } from "@/apiRequests/models/communityTalk/replyGetResponse";
import { useReplyPostStore } from "@/store/communityTalk/replyPostStore";
import { TalkRoomDto } from "@/apiRequests/models/communityTalk/talkRoomGetResponse";

interface State {
	/**
	 * トークルームに紐づくリプライ。
	 */
	replies: ReplyDto[];
	/**
	 * リプライ追加取得時のAPIステータス。
	 */
	replyGetMoreStatus: ApiGetLoadAddStatusType;
}
interface Props {
	/**
	 * トークルーム情報。
	 */
	talkRoom: TalkRoomDto;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
}

export default /*#__PURE__*/_defineComponent({
  props: {
    talkRoom: { type: null, required: true },
    wordSettings: { type: null, required: true },
    communityTalkStoreKey: { type: String, required: true }
  },
  setup(__props: any, { expose }) {
  expose()

const props = __props as {
	/**
	 * トークルーム情報。
	 */
	talkRoom: TalkRoomDto;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
}
/**
 * トークルーム情報。
 */

const state = reactive<State>({
	replies: [],
	replyGetMoreStatus: "beforeRequest",
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-undef
const { replies, replyGetMoreStatus } = toRefs(state);



const communityTalkUserViewModel: CommunityTalkUserViewModel = {
	badgeType: null,
	nickName: props.talkRoom.nickName,
	profilePicUrl: props.talkRoom.profilePicUrl,
	reviewerAttributeAnswers: props.talkRoom.reviewerAttributeAnswers,
};

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

// リプライ投稿用Store生成
// inject用のKeyを生成
const replyPostStoreKey = props.talkRoom.talkRoomId + "_replyPostStore";
// Storeを生成
const replyPostStore = useReplyPostStore();
// Storeを親コンポーネントから供給
provide(replyPostStoreKey, replyPostStore);
// replyPostStoreの初期データを登録
replyPostStore.init(props.talkRoom.talkRoomId);

/**
 * リプライ追加表示ボタンを表示するかどうか。
 */
const isShowReplyMoreButton = (): boolean => {
	return props.talkRoom.replyCount > state.replies.length;
};

/**
 * 詳細表示モードかどうか。
 */
const isDetailsMode = (): boolean => {
	// トークルーム情報が保持するリプライ詳細が0件の場合=詳細表示モードではないと判断
	if (state.replies.length === 0) {
		return false;
	}
	return true;
};

/**
 * 詳細表示モードを終了する。
 */
const closeDetails = (): void => {
	state.replies = [];
};

/**
 * リプライ追加APIリクエスト中かどうか。
 */
const isProcessingGetMoreReply = (): boolean => {
	return state.replyGetMoreStatus === ApiGetLoadAddStatus.loading || state.replyGetMoreStatus === ApiGetLoadAddStatus.addLoading;
};

const replyController = new ReplyController();
/**
 * リプライを追加表示する。
 */
const getMoreReply = async (): Promise<void> => {
	if (isProcessingGetMoreReply()) return;
	state.replyGetMoreStatus = ApiGetLoadAddStatus.loading;

	let sortKey: string | null = null;
	if (state.replies.length > 0) {
		const lastReply = state.replies[state.replies.length - 1];
		sortKey = lastReply.replySortKey;
	}

	try {
		// APIリクエスト
		const replyResponse = await replyController.getReplyData(
			communityTalkStore.getGeneralSettings().replyInitialDisplayCnt,
			communityTalkStore.getGeneralSettings().replyAdditionalDisplayCnt,
			props.talkRoom.talkRoomId,
			// 紐づくリプライが0件(=1回もトークルーム詳細ボタンが押されていない)であれば、初期表示モードとして連携
			state.replies.length === 0,
			sortKey,
			communityTalkStore.getTenantId(),
			communityTalkStore.getSubjectCd()
		);
		// データを格納
		state.replies.push(...replyResponse.data);
		state.replyGetMoreStatus = ApiGetLoadAddStatus.success;
	} catch (e) {
		state.replyGetMoreStatus = ApiGetLoadAddStatus.error;
		console.error(e);
	}
};

const __returned__ = { state, replies, replyGetMoreStatus, props, communityTalkUserViewModel, communityTalkStore, replyPostStoreKey, replyPostStore, isShowReplyMoreButton, isDetailsMode, closeDetails, isProcessingGetMoreReply, replyController, getMoreReply, ApiGetLoadWithAddMaster, TalkRoomContents, TalkRoomDate, ReplyMaster, ReplyOpenDetailsButton, ReplyList, ReplyMoreButton, NoReply, ReplyPost, CommunityTalkUser, CommunityTalkMedia, TalkRoomNice }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})